import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { Tab, Tabs } from 'react-bootstrap';

import Container from 'components/shared/Container';
import ImagesSection from 'components/product_lists/product_list/image_components/ImagesSection';
import RecommendationsSection from 'components/product_lists/product_list/image_components/RecommendationsSection';
import UploadImagesSection from 'components/product_lists/product_list/image_components/UploadImagesSection';

const SUGGESTED_THUMBNAILS_VIEW_TABS = {
  GLOBAL_PRODUCT_THUMBNAIL: 'Global Product Image',
  GOOGLE_SCRAPED: 'Google Scraped',
  L4_IMAGES: 'L4 Images',
  ML_SUGGESTED_THUMBNAILS: 'AI Suggested Thumbnails',
  UPLOAD_IMAGE: 'Upload Image',
};

function SuggestedThumbnailsView({
  temporaryProductImage,
  product,
  setSelectedThumbnailUrl,
  uploadedImages,
  setUploadedImages,
}) {
  const googleScrapedImages = useMemo(
    () =>
      product?.global_product?.default_image_urls?.map((url, index) => ({
        image_id: index,
        url: url,
      })) || [],
    [product],
  );

  const mlSuggestedThumbnailRecommondations = useMemo(() => {
    if (!temporaryProductImage?.ml_suggested_thumbnails) {
      return [];
    }
    const sortedThumbnails = [...temporaryProductImage.ml_suggested_thumbnails]
      .sort((a, b) => {
        const scoreA = a.score ?? 0;
        const scoreB = b.score ?? 0;
        return scoreB - scoreA;
      })
      .map((thumbnail, index) => ({
        image_id: index,
        url: thumbnail.url,
        caption: thumbnail.caption,
        source: thumbnail.source,
        score: thumbnail.score,
        content: thumbnail.content,
      }));
    return sortedThumbnails;
  }, [temporaryProductImage]);

  const globalProductImage = useMemo(
    () =>
      product?.global_product?.default_image_url
        ? [{ image_id: 0, url: product?.global_product?.default_image_url }]
        : [],
    [product],
  );

  const [activeTabKey, setActiveTabKey] = useState(
    mlSuggestedThumbnailRecommondations.length > 0
      ? SUGGESTED_THUMBNAILS_VIEW_TABS.ML_SUGGESTED_THUMBNAILS
      : globalProductImage.length > 0
      ? SUGGESTED_THUMBNAILS_VIEW_TABS.GLOBAL_PRODUCT_THUMBNAIL
      : googleScrapedImages.length > 0
      ? SUGGESTED_THUMBNAILS_VIEW_TABS.GOOGLE_SCRAPED
      : product?.global_product?.l4_category?.id
      ? SUGGESTED_THUMBNAILS_VIEW_TABS.L4_IMAGES
      : SUGGESTED_THUMBNAILS_VIEW_TABS.UPLOAD_IMAGE,
  );

  useEffect(() => {
    setActiveTabKey(
      mlSuggestedThumbnailRecommondations.length > 0
        ? SUGGESTED_THUMBNAILS_VIEW_TABS.ML_SUGGESTED_THUMBNAILS
        : globalProductImage.length > 0
        ? SUGGESTED_THUMBNAILS_VIEW_TABS.GLOBAL_PRODUCT_THUMBNAIL
        : googleScrapedImages.length > 0
        ? SUGGESTED_THUMBNAILS_VIEW_TABS.GOOGLE_SCRAPED
        : product?.global_product?.l4_category?.id
        ? SUGGESTED_THUMBNAILS_VIEW_TABS.L4_IMAGES
        : SUGGESTED_THUMBNAILS_VIEW_TABS.UPLOAD_IMAGE,
    );
  }, [product, googleScrapedImages, globalProductImage, mlSuggestedThumbnailRecommondations]);
  return (
    <Container>
      <Tabs
        className="mb-2 border-top-0"
        activeKey={activeTabKey}
        onSelect={selectedKey => setActiveTabKey(selectedKey)}
      >
        {mlSuggestedThumbnailRecommondations.length > 0 && (
          <Tab
            eventKey={SUGGESTED_THUMBNAILS_VIEW_TABS.ML_SUGGESTED_THUMBNAILS}
            title={SUGGESTED_THUMBNAILS_VIEW_TABS.ML_SUGGESTED_THUMBNAILS}
          >
            <ImagesSection
              images={mlSuggestedThumbnailRecommondations}
              onImageClick={image => setSelectedThumbnailUrl(image.url)}
              isImageSelected={() => {}}
            />
          </Tab>
        )}
        {product?.global_product?.default_image_url && (
          <Tab
            eventKey={SUGGESTED_THUMBNAILS_VIEW_TABS.GLOBAL_PRODUCT_THUMBNAIL}
            title={SUGGESTED_THUMBNAILS_VIEW_TABS.GLOBAL_PRODUCT_THUMBNAIL}
          >
            <ImagesSection
              images={globalProductImage}
              onImageClick={image => setSelectedThumbnailUrl(image.url)}
              isImageSelected={() => {}}
            />
          </Tab>
        )}
        {googleScrapedImages.length > 0 && (
          <Tab
            eventKey={SUGGESTED_THUMBNAILS_VIEW_TABS.GOOGLE_SCRAPED}
            title={SUGGESTED_THUMBNAILS_VIEW_TABS.GOOGLE_SCRAPED}
          >
            <ImagesSection
              images={googleScrapedImages}
              onImageClick={image => setSelectedThumbnailUrl(image.url)}
              isImageSelected={() => {}}
            />
          </Tab>
        )}
        {product?.global_product?.l4_category?.id && (
          <Tab
            eventKey={SUGGESTED_THUMBNAILS_VIEW_TABS.L4_IMAGES}
            title={SUGGESTED_THUMBNAILS_VIEW_TABS.L4_IMAGES}
          >
            <div className={classNames('my-3')}>
              <RecommendationsSection
                l4CategoryId={product?.global_product?.l4_category?.id}
                selectedThumbnailUrl={''}
                setSelectedThumbnailId={() => {}}
                setSelectedThumbnailUrl={setSelectedThumbnailUrl}
                brandId={product?.brand?.id}
                thumbnails={true}
              />
            </div>
          </Tab>
        )}
        <Tab
          eventKey={SUGGESTED_THUMBNAILS_VIEW_TABS.UPLOAD_IMAGE}
          title={SUGGESTED_THUMBNAILS_VIEW_TABS.UPLOAD_IMAGE}
        >
          <div className="my-3">
            <UploadImagesSection
              imageUrls={uploadedImages}
              setImageUrls={setUploadedImages}
              selectedThumbnailUrl={''}
              setSelectedThumbnailUrl={setSelectedThumbnailUrl}
            />
          </div>
        </Tab>
      </Tabs>
    </Container>
  );
}

SuggestedThumbnailsView.propTypes = {
  product: PropTypes.object,
  temporaryProductImage: PropTypes.object,
  setSelectedThumbnailUrl: PropTypes.func,
  uploadedImages: PropTypes.array,
  setUploadedImages: PropTypes.func,
};

export default SuggestedThumbnailsView;
